import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  ExportImportStatus,
  IExportDTO,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { exportAbsenceData, fetchExportStatus } from "./api";
import { downloadExportFile } from "./useDownloadFile";
import { AbsenceQueryKeys } from "./config";

const ExportAbsenceDataQueries = {
  exportStatus: (exportId: string | undefined) =>
    queryOptions({
      enabled: !!exportId,
      queryKey: AbsenceQueryKeys.exportStatus(exportId),
      queryFn: () => fetchExportStatus(exportId!),
      refetchInterval: (query) =>
        query.state.data?.status !== ExportImportStatus.SUCCEEDED ? 500 : false,
    }),

  export: mutationOptions({
    mutationFn: (params: IExportDTO) => exportAbsenceData(params),
  }),

  download: mutationOptions({
    mutationFn: (exportId: string) => downloadExportFile(exportId),
  }),
};

export const useExportAbsenceData = () => {
  const queryClient = useQueryClient();

  const exportMutation = useMutation({
    ...ExportAbsenceDataQueries.export,
    onSuccess: (data) => {
      queryClient.setQueryData(
        AbsenceQueryKeys.exportStatus(data.exportId),
        data
      );
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas eksportu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const downloadMutation = useMutation({
    ...ExportAbsenceDataQueries.download,
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas eksportu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const exportStatusQuery = useQuery({
    ...ExportAbsenceDataQueries.exportStatus(exportMutation.data?.exportId),
    queryFn: async () => {
      const status = await fetchExportStatus(exportMutation.data!.exportId);
      if (status.status === ExportImportStatus.SUCCEEDED) {
        downloadMutation.mutate(exportMutation.data!.exportId);
      }
      return status;
    },
  });

  return {
    exportDataMutation: exportMutation.mutate,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,
    exportStatus: exportStatusQuery.data,
    isPolling: exportStatusQuery.isLoading,
    pollingError: exportStatusQuery.error,
    isDownloading: downloadMutation.isPending,
    downloadError: downloadMutation.error,
    hasDownloadSucceeded: downloadMutation.isSuccess,
  };
};

export default ExportAbsenceDataQueries;
